import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/layout/DashboardLayout';
import API_CONFIG from '../../config/api';

interface Settings {
  _id: string;
  transportationPrice: number;
  defaultCapacity: number;
  lastUpdatedBy?: string;
  lastUpdatedAt: string;
}

const GeneralSettings: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transportationPrice, setTransportationPrice] = useState(0);
  const [defaultCapacity, setDefaultCapacity] = useState(30);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.baseURL}/settings`);
      const data = await response.json();
      
      if (data.success) {
        setSettings(data.data);
        setTransportationPrice(data.data.transportationPrice || 0);
        setDefaultCapacity(data.data.defaultCapacity || 30);
      } else {
        toast.error('خطا در دریافت تنظیمات');
      }
    } catch (error) {

      toast.error('خطا در دریافت تنظیمات');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_CONFIG.baseURL}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          transportationPrice: transportationPrice,
          defaultCapacity: defaultCapacity
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('تنظیمات با موفقیت ذخیره شد');
        setSettings(data.data);
      } else {
        toast.error(data.message || 'خطا در ذخیره تنظیمات');
      }
    } catch (error) {

      toast.error('خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
  
        <div className="container mx-auto py-8 px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">در حال بارگذاری...</p>
          </div>
        </div>
   
    );
  }

  return (
  
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-800">تنظیمات عمومی</h1>
              <p className="text-gray-600 mt-1">مدیریت تنظیمات کلی سیستم</p>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                {/* Transportation Pricing Section */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">قیمت‌گذاری ایاب و ذهاب</h2>
                  <p className="text-gray-600 mb-4">
                    این قیمت برای نمونه‌گیری در منزل محاسبه می‌شود. اگر نمونه‌گیری در آزمایشگاه انجام شود، 
                    هزینه ایاب و ذهاب رایگان خواهد بود.
                  </p>
                  
                  <div className="max-w-md">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      قیمت ایاب و ذهاب (تومان)
                    </label>
                    <input
                      type="number"
                      value={transportationPrice}
                      onChange={(e) => setTransportationPrice(Number(e.target.value))}
                      min="0"
                      step="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="قیمت را وارد کنید..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      این قیمت به مبلغ اصلی آزمایش اضافه می‌شود
                    </p>
                  </div>
                </div>

                {/* Default Capacity Section */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">ظرفیت پیش‌فرض نمونه‌گیری</h2>
                  <p className="text-gray-600 mb-4">
                    این ظرفیت برای روزهایی استفاده می‌شود که در تقویم نمونه‌گیری ظرفیت خاصی تعریف نشده باشد.
                    اگر برای روزی ظرفیت خاصی تعریف کنید، آن ظرفیت اولویت دارد.
                  </p>
                  
                  <div className="max-w-md">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ظرفیت پیش‌فرض (تعداد)
                    </label>
                    <input
                      type="number"
                      value={defaultCapacity}
                      onChange={(e) => setDefaultCapacity(Number(e.target.value))}
                      min="1"
                      step="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="ظرفیت را وارد کنید..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      تعداد حداکثر درخواست‌های قابل پذیرش در روز
                    </p>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 disabled:bg-indigo-300"
                  >
                    {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                  </button>
                </div>

                {/* Settings Info */}
                {settings && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-800 mb-2">اطلاعات تنظیمات</h3>
                    <div className="text-sm text-blue-700">
                      <p>آخرین به‌روزرسانی: {new Date(settings.lastUpdatedAt).toLocaleDateString('fa-IR')}</p>
                      <p>قیمت فعلی ایاب و ذهاب: {settings.transportationPrice.toLocaleString('fa-IR')} تومان</p>
                      <p>ظرفیت پیش‌فرض: {settings.defaultCapacity.toLocaleString('fa-IR')} درخواست در روز</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
   
  );
};

export default GeneralSettings;
